import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { adminQueryKeys } from "./queryKeys";
import {
  createListQueryInit,
  createDetailQueryInit,
  listQueryOptions,
  detailQueryOptions,
} from "./hooks";
import type { SortOrder } from "./sort";
import { likeFilter, joinOr } from "./filters";
import { useMemo } from "react";

// Type definitions for admin users
// Note: These types may not exist in generated API types until OpenAPI spec is regenerated
export type AdminUserListItem = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  createdAt?: string;
  updatedAt?: string;
  groups?: Array<{
    group: {
      id: string;
      name: string;
      type: string;
    };
  }>;
};

export type AdminUserDetail = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  createdAt?: string;
  updatedAt?: string;
  groups?: Array<{
    group: {
      id: string;
      name: string;
      type: string;
    };
  }>;
};

type AdminUsersListQuery = {
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortOrder: SortOrder;
  filterText?: string;
};

export function useAdminUsersList(params: AdminUsersListQuery) {
  const filterText = params.filterText?.trim() ?? "";
  const filter = useMemo(() => {
    if (filterText === "") return undefined;
    // Support filtering by email or name
    return joinOr([
      likeFilter("email", filterText),
      likeFilter("firstName", filterText),
      likeFilter("lastName", filterText),
    ]);
  }, [filterText]);
  const init = createListQueryInit(
    { ...params, filter },
    "createdAt",
  );
  return apiClient.useQuery("get", "/api/admin/users" as any, init, listQueryOptions);
}

export function useAdminUser(userId: string | undefined) {
  const init = createDetailQueryInit(userId);
  return apiClient.useQuery(
    "get",
    "/api/admin/users/{id}" as any,
    init,
    detailQueryOptions(!!userId),
  );
}

export function useAdminUserCreate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/users" as any, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.users.list,
      });
    },
  });
}

export function useAdminUserUpdate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("patch", "/api/admin/users/{id}" as any, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.users.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.users.detail,
      });
    },
  });
}

export function useAdminUserResetPassword() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/users/{id}/reset-password" as any, {
    onSuccess: async () => {
      // Invalidate user queries in case we want to show last reset time in the future
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.users.detail,
        exact: false,
      });
    },
  });
}

